//angular imports
import { ActivatedRoute, NavigationStart, Router } from "@angular/router";

//local imports
import {
  BreadcrumbChangedMessage,
  BreadcrumbItemToggleMessage,
  IBreadcrumbItemModel,
  Messenger,
} from "@app/shared";
import {
  CatalogConstants,
  CategoryModel,
  DepartmentModel,
  ProgramModel,
  ProjectModel,
  RoomModel,
  CatalogStateService,
  RoomContentModel
} from "@app/catalog";
import {
  CatalogCommonConstants,
  SelectCategoryMessage,
  SelectDepartmentMessage,
  SelectProgramMessage,
  SelectProjectMessage,
  SelectRoomContentMessage,
  SelectRoomMessage,
} from "@app/catalog-common";
import { SearchModel } from "@app/core";
import { CommonConstants } from "@app/app-common";
import { distinctUntilChanged, filter, Subject, takeUntil } from "rxjs";

export class CatalogRouteStateHandler {

    private catalogStateService: CatalogStateService;
    private messenger: Messenger;
    private router: Router;
    private currentBreadcrumbs: IBreadcrumbItemModel[] = [];
    private readonly destroying$ = new Subject<void>();
    private isBrowserNavigation = false;



    constructor(
    ) {}

    public initialize(
        catalogStateService: CatalogStateService, 
        messenger: Messenger, 
        route: ActivatedRoute, 
        router: Router) {

        this.catalogStateService = catalogStateService;
        this.messenger = messenger;
        this.router = router;

        messenger.subscribe(this, BreadcrumbChangedMessage, (msg) => {
            if(msg.breadcrumbName !== CatalogConstants.CATALOG) return;
            this.currentBreadcrumbs = msg.breadcrumbs;
            this.syncBreadcrumbsToRoute(msg.breadcrumbs, route);
        });

        this.router.events.pipe(
            takeUntil(this.destroying$),
            filter(event => event instanceof NavigationStart),
            distinctUntilChanged((prev, curr) => 
            prev.url === curr.url && prev.navigationTrigger === curr.navigationTrigger
        )
            ).subscribe((event: NavigationStart) => {
            // Check if navigation was triggered by browser back/forward button
            this.isBrowserNavigation = event.navigationTrigger === 'popstate';
            });

        this.initializeStateFromRoute(route);
    }

    private syncBreadcrumbsToRoute(breadcrumbs: IBreadcrumbItemModel[], route: ActivatedRoute): void {
        const categoryIds: string[] = [];
        const departmentIds: string[] = [];
        const roomIds: string[] = [];
        const programIds: string[] = [];
        const projectIds: string[] = [];
        const roomContentIds: string[] = [];
        const searchList: string[] = [];

        breadcrumbs.forEach(item => {
            if (item instanceof RoomModel) {
                roomIds.push(item.id);
            } 
            else if (item instanceof DepartmentModel) {
                departmentIds.push(item.id);
            } 
            else if (item instanceof CategoryModel) {
                categoryIds.push(item.id);
            } 
            else if (item instanceof ProgramModel) {
                programIds.push(item.id);
            } 
            else if (item instanceof ProjectModel) {
                projectIds.push(item.id);
            } else if (item instanceof RoomContentModel) {
                roomContentIds.push(item.id);
            } else if (item instanceof SearchModel) {
                searchList.push(item.name());
            }
        });

        const currentRoute = this.getCurrentActivatedRoute();
        const queryParams: any = {
            categoryId: categoryIds.length > 0 ? categoryIds.join(',') : null,
            departmentId: departmentIds.length > 0 ? departmentIds.join(',') : null,
            roomId: roomIds.length > 0 ? roomIds.join(',') : null,
            programId: programIds.length > 0 ? programIds.join(',') : null,
            projectId: projectIds.length > 0 ? projectIds.join(',') : null,
            roomContentId: roomContentIds.length > 0 ? roomContentIds.join(',') : null,
            search: searchList.length > 0 ? searchList.join(','): null,
        };

 

        const hasSearch = searchList.length > 0;
        if(this.isBrowserNavigation) {
            this.isBrowserNavigation = false;
            return;
        }

        
        this.router.navigate([], {
            relativeTo: currentRoute,
            queryParams,
            queryParamsHandling: hasSearch ? '' : 'merge',
        });
    }

    public syncRouteWithBreadcrumbs(route: ActivatedRoute): void {
        if(!this.isBrowserNavigation){
            return;
        }
        const queryParams = route.snapshot.queryParams;
        const parseList = (key: string) => (queryParams[key] as string || '').split(',').filter(id => !!id);

        const departmentIds = parseList(CatalogCommonConstants.ROUTE_PARAMS.DEPARTMENT_ID);
        const roomIds       = parseList(CatalogCommonConstants.ROUTE_PARAMS.ROOM_ID);
        const projectIds    = parseList(CatalogCommonConstants.ROUTE_PARAMS.PROJECT_ID);
        const programIds    = parseList(CatalogCommonConstants.ROUTE_PARAMS.PROGRAM_ID);
        const roomContentIds = parseList(CatalogCommonConstants.ROUTE_PARAMS.ROOM_CONTENT_ID);
        const searchList    = parseList(CatalogCommonConstants.ROUTE_PARAMS.SEARCH_KEY);

        const rooms = this.catalogStateService.CatalogManagementState.menus().rooms().filter(room => {
            return roomIds.includes(room.id);
        });

        const departments = this.catalogStateService.CatalogManagementState.menus().departments().filter(dept => {
            return departmentIds.includes(dept.id);
        });

        const projects = this.catalogStateService.CatalogManagementState.menus().projects().filter(proj => {
            return projectIds.includes(proj.id);
        });

        const programs = this.catalogStateService.CatalogManagementState.menus().programs().filter(prog => {
            return programIds.includes(prog.id);
        });

        const roomContents = this.catalogStateService.CatalogManagementState.menus().roomContents().filter(content => {
            return roomContentIds.includes(content.id);
        });


        //Remove other breadcrumbs
        this.currentBreadcrumbs.forEach(cb => {
            console.log({cb})
            this.messenger.send(new BreadcrumbItemToggleMessage(cb, CatalogConstants.CATALOG));
        });

        if(rooms.length) {
            this.messenger.send(new BreadcrumbItemToggleMessage(rooms[0], CatalogConstants.CATALOG));
        }
      
        if(departments.length) {
            this.messenger.send(new BreadcrumbItemToggleMessage(departments[0], CatalogConstants.CATALOG));
        }

         if(projects.length) {
            this.messenger.send(new BreadcrumbItemToggleMessage(projects[0], CatalogConstants.CATALOG));
        }

          if(programs.length) {
            this.messenger.send(new BreadcrumbItemToggleMessage(programs[0], CatalogConstants.CATALOG));
        }

         if(roomContents.length) {
            this.messenger.send(new BreadcrumbItemToggleMessage(roomContents[0], CatalogConstants.CATALOG));
        }

        searchList.forEach(val => {
            console.log(val);
            const model = new SearchModel();
            model.id = crypto.randomUUID();
            model.name.set(val);
            this.messenger.send(new BreadcrumbItemToggleMessage(model, CommonConstants.CATALOG));
        })
    }

    private getCurrentActivatedRoute(): ActivatedRoute {
        let route = this.router.routerState.root;
        while (route) {
            if (route.firstChild === null) {
                return route;
            }
            route = route.firstChild;
        }
        return route;
    }


    public async initializeStateFromRoute(route: ActivatedRoute): Promise<void> {
        const queryParams = route.snapshot.queryParams;


        const parseList = (key: string) => (queryParams[key] as string || '').split(',').filter(id => !!id);

        const categoryIds   = parseList(CatalogCommonConstants.ROUTE_PARAMS.CATEGORY_ID);
        const departmentIds = parseList(CatalogCommonConstants.ROUTE_PARAMS.DEPARTMENT_ID);
        const roomIds       = parseList(CatalogCommonConstants.ROUTE_PARAMS.ROOM_ID);
        const programIds    = parseList(CatalogCommonConstants.ROUTE_PARAMS.PROGRAM_ID);
        const projectIds    = parseList(CatalogCommonConstants.ROUTE_PARAMS.PROJECT_ID);
        const roomContentIds = parseList(CatalogCommonConstants.ROUTE_PARAMS.ROOM_CONTENT_ID);
        const searchList    = parseList(CatalogCommonConstants.ROUTE_PARAMS.SEARCH_KEY);


        await this.catalogStateService.loadCarousels({
        roomId: roomIds.length > 0 ? roomIds[0] : null,
        departmentId: departmentIds.length > 0 ? departmentIds[0] : null,
        programId: programIds.length > 0 ? programIds[0] : null,
        projectId: projectIds.length > 0 ? projectIds[0] : null,
        roomContentId: roomContentIds.length > 0 ? roomContentIds[0] : null,
        categoryId: categoryIds.length > 0 ? categoryIds[0] : null,
        searchValues: searchList,
        });
    
        



        const categories = this.catalogStateService.CatalogManagementState.menus().categories().filter(item => categoryIds.includes(item.id));
        const departments = this.catalogStateService.CatalogManagementState.menus().departments().filter(item => departmentIds.includes(item.id));
        const rooms = this.catalogStateService.CatalogManagementState.menus().rooms().filter(item => roomIds.includes(item.id));
        const programs = this.catalogStateService.CatalogManagementState.menus().programs().filter(item => programIds.includes(item.id));
        const projects = this.catalogStateService.CatalogManagementState.menus().projects().filter(item => projectIds.includes(item.id));
        const roomContents = this.catalogStateService.CatalogManagementState.menus().roomContents().filter(item => roomContentIds.includes(item.id));



        searchList.forEach(val => {
            const model = new SearchModel();
            model.id = crypto.randomUUID();
            model.name.set(val);
            this.messenger.send(new BreadcrumbItemToggleMessage(model, CommonConstants.CATALOG));
        });
        if(categories.length) {
            this.messenger.send(new SelectCategoryMessage(categories[0]));
        }

        if(departments.length) {
            this.messenger.send(new SelectDepartmentMessage(departments[0]));
        }

        if(rooms.length) {
            this.messenger.send(new SelectRoomMessage(rooms[0]));
        }

        if(programs.length) {
            this.messenger.send(new SelectProgramMessage(programs[0]));
        }

        if(projects.length) {
            this.messenger.send(new SelectProjectMessage(projects[0]));
        }

        if(roomContents.length) {
            this.messenger.send(new SelectRoomContentMessage(roomContents[0]));
        }
    }
}